import InstagramGalleryQuery from "@apps/instagram/queries/InstagramGalleryQuery";
import InstagramPostsQuery from "@apps/instagram/queries/InstagramPostsQuery";
import InstagramStoriesQuery from "@apps/instagram/queries/InstagramStoriesQuery";
import InstagramTaggedProductQuery from "@apps/instagram/queries/InstagramTaggedProductQuery";
import InstagramGalleryService from "@apps/instagram/services/InstagramGalleryService";
import InstagraGalleryServiceInterface from "@apps/instagram/services/InstagramGalleryService/InstagramGalleryServiceInterface";
import InstagramPostsService from "@apps/instagram/services/InstagramPostsService";
import InstagramPostsServiceInterface from "@apps/instagram/services/InstagramPostsService/InstagramPostsServiceInterface";
import InstagramStoriesService from "@apps/instagram/services/InstagramStoriesService/InstagramStoriesService";
import InstagramTaggedProductService from "@apps/instagram/services/InstagramTaggedProductService";
import InstagramTrackersApiService from "@apps/instagram/services/InstagramTrackersService";
import InstagramGalleryStore from "@apps/instagram/stores/InstagramGalleryStore";
import InstagramPostsStore from "@apps/instagram/stores/InstagramPostsStore";
import InstagramStoriesStore from "@apps/instagram/stores/InstagramStoriesStore";
import InstagramTaggedProductStore from "@apps/instagram/stores/InstagramTaggedProductStore";
import {
    instagramAuthApiClientToken,
    instagramAuthApiV3ClientToken,
} from "@apps/instagram/tokens";
import { BaseModule } from "@modules/base_module/BaseModule";
import { CartModule } from "@modules/cart/CartModule";
import { CartQuery } from "@modules/cart/queries/CartQuery";
import { CartService } from "@modules/cart/services/CartService";
import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
import { CommonModule } from "@modules/common_module/CommonModule";
import LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
import { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
import { I18nModule } from "@modules/i18n/I18nModule";
import { LegacyTrackingModule } from "@modules/legacy_tracking/LegacyTrackingModule";
import { TrackingApiServiceInterface } from "@modules/legacy_tracking/services/TrackingApiServiceInterface";
import HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
import { MoneyModule } from "@modules/money/MoneyModule";
import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import {
    productsQueryToken,
    productsServiceToken,
} from "@modules/products/tokens";
import { assert } from "superstruct";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { WIDGET_TYPE } from "./constants";
import InstagramGalleryWidgetComponent from "./InstagramGalleryWidget.svelte";
import {
    InstagramGalleryWidgetConfigInterface,
    InstagramGalleryWidgetConfigSchema,
} from "./InstagramGalleryWidgetConfig";

class InstagramGalleryWidget {
    galleryStore: InstagramGalleryStore;
    galleryQuery: InstagramGalleryQuery;
    galleryService: InstagramGalleryService;
    postsStore: InstagramPostsStore;
    postsService: InstagramPostsService;
    postsQuery: InstagramPostsQuery;
    taggedProductStore: InstagramTaggedProductStore;
    taggedProductService: InstagramTaggedProductService;
    taggedProductQuery: InstagramTaggedProductQuery;
    instagramStoriesQuery: InstagramStoriesQuery;
    instagramStoriesService: InstagramStoriesService;
    instagramStoriesStore: InstagramStoriesStore;
    instagramTrackersService: InstagramTrackersApiService;
    locationController: LocationControllerInterface;
    cartService: CartService;
    cartQuery: CartQuery;
    galleryServiceAdapter: InstagraGalleryServiceInterface | null;
    postsServiceAdapter: InstagramPostsServiceInterface | null;
    trackingApiService: TrackingApiServiceInterface;
    widgetName: WIDGET_TYPE;
    gwi18n: GwI18n;
    productsService: ProductsServiceInterface;
    urlsBuilder: URLsBuilder;
    productsQuery: ProductsQuery;
    htmlSanitizer: HTMLSanitizerInterface;
    cartAvailabilityProvider: CartAvailabilityProvider;

    constructor(
        private config: InstagramGalleryWidgetConfigInterface,
        private logger: LoggerInterface,
        private _widgetName: WIDGET_TYPE
    ) {
        this.logger.debug("InstagramGalleryWidget.constructor", {
            config,
        });
        assert(config, InstagramGalleryWidgetConfigSchema);

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "instagram.critical.InstagramGalleryWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "instagram.critical.InstagramGalleryWidget"
            )
        );

        // galleries store/query/service
        const api = container.resolve(instagramAuthApiClientToken);
        const apiV3 = container.resolve(instagramAuthApiV3ClientToken);
        this.trackingApiService =
            container.resolve(LegacyTrackingModule).exports.trackingApiService;
        this.locationController =
            container.resolve(CommonModule).exports.locationController;
        this.cartService = container.resolve(CartModule).exports.cartService;
        this.cartQuery = container.resolve(CartModule).exports.cartQuery;
        this.galleryStore = new InstagramGalleryStore();
        this.galleryService = new InstagramGalleryService(
            this.galleryStore,
            apiV3,
            this.logger
        );
        this.galleryQuery = new InstagramGalleryQuery(this.galleryStore);
        // posts store/query/service
        this.postsStore = new InstagramPostsStore();
        this.postsService = new InstagramPostsService(
            this.postsStore,
            api,
            this.logger
        );
        this.postsQuery = new InstagramPostsQuery(this.postsStore);
        // tagged product store/query/service for product view
        this.taggedProductStore = new InstagramTaggedProductStore();
        this.taggedProductService = new InstagramTaggedProductService(
            this.taggedProductStore,
            api,
            this.logger
        );
        this.taggedProductQuery = new InstagramTaggedProductQuery(
            this.taggedProductStore
        );
        // stories store/query/service
        this.instagramStoriesStore = new InstagramStoriesStore();
        this.instagramStoriesService = new InstagramStoriesService(
            this.instagramStoriesStore,
            apiV3,
            this.logger
        );
        this.instagramStoriesQuery = new InstagramStoriesQuery(
            this.instagramStoriesStore
        );
        // tracker service
        this.instagramTrackersService = new InstagramTrackersApiService(
            api,
            this.logger
        );
        // adapters

        this.widgetName = _widgetName;

        this.gwi18n = i18nModule.exports.i18n;
        this.urlsBuilder = container.resolve(CommonModule).exports.urlsBuilder;
        this.productsService = container.resolve(productsServiceToken);
        this.productsQuery = container.resolve(productsQueryToken);
        this.htmlSanitizer =
            container.resolve(MoneyModule).exports.htmlSanitizer;
        this.cartAvailabilityProvider =
            container.resolve(CommonModule).exports.cartAvailabilityProvider;
        this.galleryServiceAdapter = null;
        this.postsServiceAdapter = null;
    }
    init(): this {
        const element = this.config.target;
        if (!this.galleryServiceAdapter || !this.postsServiceAdapter) {
            throw new Error(
                "Somthing went wrong! galleryServiceAdapter & postsServiceAdapter not found"
            );
        }

        const modulesStatusesProvider =
            container.resolve(BaseModule).exports.modulesStatusesProvider;

        new InstagramGalleryWidgetComponent({
            target: element as HTMLElement,
            props: {
                galleryId: this.config.galleryId,
                galleryLayout: this.config.galleryLayout,
                customSettings: this.config.customSettings,
                locationController: this.locationController,
                cartService: this.cartService,
                cartQuery: this.cartQuery,
                widgetName: this.widgetName,
                gwi18n: this.gwi18n,
                urlsBuilder: this.urlsBuilder,
                htmlSanitizer: this.htmlSanitizer,
                //
                instagramGalleryQuery: this.galleryQuery,
                instagramGalleryService: this.galleryServiceAdapter,
                //
                instagramPostsQuery: this.postsQuery,
                instagramPostsStore: this.postsStore,
                instagramPostsService: this.postsServiceAdapter,
                //
                taggedProductService: this.taggedProductService,
                taggedProductQuery: this.taggedProductQuery,
                //
                instagramStoriesQuery: this.instagramStoriesQuery,
                instagramStoriesStore: this.instagramStoriesStore,
                instagramStoriesService: this.instagramStoriesService,
                //
                instagramTrackersService: this.instagramTrackersService,
                //
                trackingApiService: this.trackingApiService,
                productsService: this.productsService,
                productsQuery: this.productsQuery,
                cartAvailabilityProvider: this.cartAvailabilityProvider,
                modulesStatusesProvider: modulesStatusesProvider,
            },
        });
        return this;
    }
}

export default InstagramGalleryWidget;
